/**
 * Everything the site footer says, transcribed from the published pages.
 *
 * None of it is derivable. The five columns, their order, the split of Products
 * into two lists, which rows read "View all" and which destinations open a new
 * tab are editorial choices that live only in the CMS's page template, the
 * same treatment `BlogListingPage` gives its category filter order. Read off
 * the footer of https://www.webshare.io/blog.
 *
 * Two labels carry a non-breaking space, so they wrap as the published ones do.
 */

export type FooterLink = {
  label: string;
  href: string;
  /** The published anchor carries `target="_blank"`. */
  newTab?: boolean;
  /** The row that closes a column: it does not dim, and underlines on hover. */
  viewAll?: boolean;
};

export type FooterColumn = {
  title: string;
  /**
   * Products is authored as two lists rather than one, which is what opens the
   * gap in the middle of that column.
   */
  lists: FooterLink[][];
};

/** The blog, whose route takes no parameter: see `FooterNav`. */
export const FOOTER_BLOG_ROUTE = "/blog";

/** Root-relative, not the published host: see `sitePath` in `../marketingLinks`. */
const SITE = "";

/**
 * The sign-up door, which this app does serve. The published footer reaches it
 * through `dashboard.webshare.io`; the `?source=` key is unchanged.
 */
const REGISTER = "/register";

/** The Company column's first four rows, which are the whole of the small footer. */
export const COMPANY_LINKS: FooterLink[] = [
  { label: "About Us", href: "/about-us" },
  { label: "Intellectual Property", href: "/intellectual-property" },
  { label: "Terms of\u00A0Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy-policy" },
];

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: "Products",
    lists: [
      [
        { label: "Proxy Server", href: `${SITE}/proxy-server` },
        {
          label: "Static Residential Proxy",
          href: `${SITE}/static-residential-proxy`,
        },
        {
          label: "Rotation Residential Proxy",
          href: `${SITE}/residential-proxy`,
        },
      ],
      [
        {
          label: "Private Proxy Servers",
          href: `${SITE}/private-datacenter-proxy`,
        },
        {
          label: "Dedicated Proxy Servers",
          href: `${SITE}/dedicated-datacenter-proxy`,
        },
        {
          label: "Private Static Residential Proxies",
          href: `${SITE}/private-static-residential-proxy`,
        },
        {
          label: "Dedicated Static Residential Proxies",
          href: `${SITE}/dedicated-static-residential-proxy`,
        },
      ],
    ],
  },
  {
    title: "Features",
    lists: [
      [
        { label: "Free Proxy", href: `${SITE}/features/free-proxy` },
        { label: "Locations", href: `${SITE}/features/proxy-locations` },
        { label: "Private Proxy", href: `${SITE}/private-proxy` },
        { label: "Dedicated Proxy", href: `${SITE}/dedicated-proxy` },
        { label: "SOCKS5 Proxy", href: `${SITE}/features/socks5-proxy` },
        { label: "View All Features", href: `${SITE}/features`, viewAll: true },
      ],
    ],
  },
  {
    title: "Use Cases",
    lists: [
      [
        {
          label: "Ecommerce",
          href: `${SITE}/use-cases/ecommerce-proxies`,
          newTab: true,
        },
        {
          label: "Social Media",
          href: `${SITE}/use-cases/social-media-proxies`,
          newTab: true,
        },
        {
          label: "SEO",
          href: `${SITE}/use-cases/seo-proxies`,
          newTab: true,
        },
        {
          label: "Market Research",
          href: `${SITE}/use-cases/proxies-for-market-research`,
          newTab: true,
        },
        {
          label: "Cyber Security",
          href: `${SITE}/use-cases/proxies-for-cyber-security`,
          newTab: true,
        },
        {
          label: "View All Use Cases",
          href: `${SITE}/use-cases`,
          viewAll: true,
        },
      ],
    ],
  },
  {
    title: "Resources",
    lists: [
      [
        { label: "Help Center", href: "https://help.webshare.io/" },
        { label: "Affiliate Program", href: `${SITE}/affiliate-program` },
        { label: "System Status", href: "https://status.webshare.io/" },
        {
          label: "API\u00A0Docs",
          href: "https://apidocs.webshare.io/",
          newTab: true,
        },
        { label: "Blog", href: FOOTER_BLOG_ROUTE },
        { label: "FAQ", href: `${SITE}/frequently-asked-questions` },
      ],
    ],
  },
  {
    // Every row of this column is a page this app now serves, so all five are
    // root-relative like the published footer's own. `FooterNav` turns them
    // into route links; before `/$legalSlug` existed they carried the host
    // because on this origin they would have 404'd.
    title: "Company",
    lists: [
      [
        ...COMPANY_LINKS,
        { label: "Compliance Policy", href: "/compliance-policy" },
      ],
    ],
  },
];

/**
 * The brand block. The wordmark's `#` is the published href, not a placeholder
 * we left behind; the offer's query string is how the CMS attributes the signup.
 */
export const FOOTER_BRAND = {
  wordmarkHref: "#",
  wordmarkSrc: "/marketing-footer/webshare-wordmark-white.svg",
  offer: {
    href: `${REGISTER}?source=footer_free_proxies_banner_register`,
    lead: "Buy fast & affordable proxy servers.",
    highlight: "Get 10 proxies today for free.",
  },
  extension: {
    href: "https://chromewebstore.google.com/detail/webshare-proxy-extension/bdokeillmfmaogjpficejjcjekcflkdh",
    label: "Download our Proxy Server Extension",
  },
};

/** The published marks, vendored so the band does not reach for the CMS host. */
export const FOOTER_PAYMENT_METHODS = [
  { name: "Visa", src: "/marketing-footer/visa.svg" },
  { name: "Mastercard", src: "/marketing-footer/mastercard.svg" },
  { name: "American Express", src: "/marketing-footer/amex.svg" },
  { name: "Discover", src: "/marketing-footer/discover.svg" },
];
